import * as rules from '@vee-validate/rules';
import { createScopedI18n } from 'zone/i18n';
import messages from 'zone/locales/validations/index.json';

// Validation of the Zone forms, for vee-validate's `validationSchema` (`useForm`) or the rules
// of `useField`. Each rule is a factory returning `(value) => true | message`, the message already
// translated from locales/validations/index.json, and `chain` runs several, collecting every failure:
//
//   validationSchema: {
//     name: chain([required(), max(60)]),
//     email: chain([required(), email(), (value) => value.endsWith('@hubstaff.com') || t('company_domain')]),
//     description: max(2000),
//   }
//
// Nothing is global (no `defineRule`): a component imports what it uses, and a rule of one
// component is an inline `(value) => true | message` in the same chain. Like vee-validate's own
// rules, everything but `required` passes an empty value, so `required()` decides whether the
// field may be left blank.
const { t } = createScopedI18n(messages);

export const required = () => (value) => rules.required(value) || t('required');
export const email = () => (value) => rules.email(value) || t('email');
export const url = () => (value) => rules.url(value, []) || t('url');
export const numeric = () => (value) => !rules.required(value) || Number.isFinite(Number(value)) || t('numeric');
export const integer = () => (value) => rules.integer(value) || t('integer');
export const min = (min) => (value) => rules.min(value, [min]) || t('min', { min });
export const max = (max) => (value) => rules.max(value, [max]) || t('max', { max });
export const minValue = (min) => (value) => rules.min_value(value, [min]) || t('min_value', { min });
export const maxValue = (max) => (value) => rules.max_value(value, [max]) || t('max_value', { max });
export const between = (min, max) => (value) => rules.between(value, [min, max]) || t('between', { min, max });
export const oneOf = (list) => (value) => rules.one_of(value, list) || t('one_of');
export const regex = (pattern) => (value) => {
  pattern.lastIndex = 0;

  return rules.regex(value, [pattern]) || t('regex');
};
export const positive = () => (value) => !rules.required(value) || (Number.isFinite(Number(value)) && Number(value) > 0) || t('positive');

/**
 * Runs every rule and returns the failures: `errors.name` shows the first, `errorBag.name` has them
 * all, and none is valid to vee-validate. Every rule runs, so an inline rule must pass an empty value
 * like the basic ones do (`!value || …`).
 */
export const chain = (rules) => (value) => rules.map(rule => rule(value)).filter(result => result !== true);
